import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { resendOtpByEmail, resendOtpByPhone, verifyOtp } from '../api/messaging';
import { noAutofillProps } from './noAutofillProps';
import {
  OTP_RESEND_COOLDOWN_MS,
  getOtpResendStorageKey,
  getRetryAfterSeconds,
  isOtpCooldownError,
  markOtpResendCooldown,
} from './otpCooldown';
import './ForgotPasswordModal.scss';

const inflightForgotOtp = new Map();

function maskDestination(channel, identifier) {
  if (channel === 'email') {
    const [name, domain] = String(identifier).split('@');
    if (!name || !domain) return identifier;
    const visible = name.slice(0, 2);
    const hidden = '*'.repeat(Math.max(1, name.length - visible.length));
    return `${visible}${hidden}@${domain}`;
  }
  const digits = String(identifier).replace(/\D/g, '').slice(-10);
  return `+91 ******${digits.slice(-4)}`;
}

function formatCountdown(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function apiErrorMessage(error, fallback) {
  return error?.response?.data?.msg
    || error?.response?.data?.message
    || error?.message
    || fallback;
}

function requestForgotOtp(storageKey, request) {
  const existing = inflightForgotOtp.get(storageKey);
  if (existing) return existing;
  const pending = request().finally(() => {
    inflightForgotOtp.delete(storageKey);
  });
  inflightForgotOtp.set(storageKey, pending);
  return pending;
}

export function ForgotPasswordModal({ channel, identifier, onClose, onSuccess }) {
  const isEmail = channel === 'email';
  const storageKey = getOtpResendStorageKey({
    purpose: 'forgotPassword',
    type: isEmail ? 'email' : 'phone',
    identifier,
  });
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('sending');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageIsError, setMessageIsError] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const destination = maskDestination(channel, identifier);
  const busy = status === 'sending' || submitting;

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  useEffect(() => {
    let cancelled = false;

    const applyCooldown = (ms) => {
      const seconds = Math.max(1, Math.ceil(ms / 1000));
      markOtpResendCooldown(storageKey, seconds * 1000);
      if (!cancelled) setSecondsLeft(seconds);
    };

    const sendCode = async () => {
      if (!cancelled) {
        setStatus('sending');
        setMessage('');
        setMessageIsError(false);
      }

      try {
        const response = await requestForgotOtp(storageKey, () => (
          isEmail
            ? resendOtpByEmail({ email: identifier, purpose: 'forgotPassword' })
            : resendOtpByPhone({ phone: identifier, purpose: 'forgotPassword' })
        ));
        if (cancelled) return;
        const cooldownSec = Number(response?.data?.cooldownSeconds);
        applyCooldown(Number.isFinite(cooldownSec) && cooldownSec > 0
          ? cooldownSec * 1000
          : OTP_RESEND_COOLDOWN_MS);
        setStatus('ready');
      } catch (error) {
        if (cancelled) return;
        if (isOtpCooldownError(error)) {
          const retryAfter = getRetryAfterSeconds(error) ?? Math.ceil(OTP_RESEND_COOLDOWN_MS / 1000);
          applyCooldown(retryAfter * 1000);
          setStatus('ready');
          setMessageIsError(false);
          setMessage('A code is already active. Enter it below.');
          return;
        }
        setStatus('error');
        setMessageIsError(true);
        setMessage(apiErrorMessage(error, 'Could not send the code. Please try again.'));
      }
    };

    sendCode();
    return () => {
      cancelled = true;
    };
  }, [identifier, isEmail, storageKey]);

  const handleResend = async () => {
    if (busy || secondsLeft > 0) return;
    setStatus('sending');
    setMessage('');
    setMessageIsError(false);
    try {
      const response = isEmail
        ? await resendOtpByEmail({ email: identifier, purpose: 'forgotPassword' })
        : await resendOtpByPhone({ phone: identifier, purpose: 'forgotPassword' });
      const cooldownSec = Number(response?.data?.cooldownSeconds);
      const seconds = Number.isFinite(cooldownSec) && cooldownSec > 0
        ? Math.ceil(cooldownSec)
        : Math.ceil(OTP_RESEND_COOLDOWN_MS / 1000);
      markOtpResendCooldown(storageKey, seconds * 1000);
      setSecondsLeft(seconds);
      setStatus('ready');
      setMessageIsError(false);
      setMessage(isEmail ? 'A new code was sent to your email.' : 'A new OTP was sent to your mobile.');
    } catch (error) {
      if (isOtpCooldownError(error)) {
        const retryAfter = getRetryAfterSeconds(error) ?? Math.ceil(OTP_RESEND_COOLDOWN_MS / 1000);
        markOtpResendCooldown(storageKey, retryAfter * 1000);
        setSecondsLeft(retryAfter);
        setStatus('ready');
        setMessageIsError(false);
        setMessage('A code is already active. Enter it below.');
        return;
      }
      setStatus('ready');
      setMessageIsError(true);
      setMessage(apiErrorMessage(error, 'Could not resend the code. Please try again.'));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const otpDigits = otp.replace(/\D/g, '');
    if (!/^\d{6}$/.test(otpDigits)) {
      setMessageIsError(true);
      setMessage('Enter the 6-digit code.');
      return;
    }
    if (password.length < 6) {
      setMessageIsError(true);
      setMessage('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setMessageIsError(true);
      setMessage('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    setMessage('');
    setMessageIsError(false);
    try {
      const verified = await verifyOtp(
        isEmail
          ? { email: identifier, otp: Number(otpDigits), type: 'forgotPassword', channel: 'email' }
          : { phone: identifier, otp: Number(otpDigits), type: 'forgotPassword', channel: 'sms' }
      );
      const token = verified?.data?.token;
      if (!token) {
        throw new Error('Could not verify the code.');
      }
      await axios.put(
        '/users/set-password',
        { password, confirmPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess('Password updated. Sign in with your new password.');
    } catch (error) {
      setMessageIsError(true);
      setMessage(apiErrorMessage(error, 'Could not update your password. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={() => !busy && onClose()}>
      <div
        className={`auth-modal${busy ? ' auth-busy' : ''}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-labelledby="forgot-password-title"
        aria-busy={busy}
      >
        <div className="auth-modal-header">
          <h3 id="forgot-password-title">Forgot password</h3>
          <button type="button" className="auth-modal-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>
        <form onSubmit={handleSubmit} className="auth-modal-body" autoComplete="off">
          <p className="auth-modal-hint">
            {status === 'sending'
              ? `Sending a code to ${destination}...`
              : status === 'error'
                ? `We could not send a code to ${destination}.`
                : `We sent a 6-digit code to ${destination}. Enter it below and choose a new password.`}
          </p>

          <div className="form-group">
            <input
              type="text"
              className="form-control mobileNumberInput"
              placeholder="6-digit code"
              inputMode="numeric"
              maxLength="6"
              value={otp}
              onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={status !== 'ready' || submitting}
              {...noAutofillProps()}
            />
            <span className="placeholderAlternative mobileNumber">OTP</span>
            <i className="bar"></i>
          </div>

          <div className="form-group">
            <input
              type="password"
              className="form-control mobileNumberInput"
              placeholder="New password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={status !== 'ready' || submitting}
              {...noAutofillProps({ autoComplete: 'new-password' })}
            />
            <span className="placeholderAlternative mobileNumber">New password</span>
            <i className="bar"></i>
          </div>

          <div className="form-group">
            <input
              type="password"
              className="form-control mobileNumberInput"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              disabled={status !== 'ready' || submitting}
              {...noAutofillProps({ autoComplete: 'new-password' })}
            />
            <span className="placeholderAlternative mobileNumber">Confirm password</span>
            <i className="bar"></i>
          </div>

          <div className="forgot-password-resend">
            {secondsLeft > 0 ? (
              <span>Resend code in {formatCountdown(secondsLeft)}</span>
            ) : (
              <button type="button" onClick={handleResend} disabled={busy || status === 'error'}>
                Resend code
              </button>
            )}
          </div>

          {message && (
            <div className={`responseMessage ${messageIsError ? '' : 'responseMessage--success'}`}>
              <span>{message}</span>
            </div>
          )}

          <div className="auth-modal-footer">
            <button type="button" className="auth-modal-btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            {status === 'error' ? (
              <button type="button" className="auth-modal-btn-primary" onClick={handleResend} disabled={busy}>
                Try again
              </button>
            ) : (
              <button type="submit" className="auth-modal-btn-primary" disabled={busy || status !== 'ready'}>
                {submitting ? 'Updating...' : status === 'sending' ? 'Sending...' : 'Update password'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
